import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import TrodoLoader from 'shared/components/ui/TrodoLoader';
import DrawerScrim, { DRAWER_OVERLAY_CLASS } from 'shared/components/ui/DrawerScrim';
import AgentRunDetailPage from 'features/agent-analytics/pages/AgentRunDetailPage';
import Sparkline from 'features/issues/components/Sparkline';
import MetaGrid from 'shared/components/ui/MetaGrid';
import AskIcon from 'shared/components/ui/AskIcon';
import TableFooter from 'shared/components/tables/TableFooter';
import { formatDate, formatDuration, formatRelativeTime } from 'features/agent-analytics/components/runCells';
import FailureTags, { Tag } from 'features/agent-analytics/components/failures/FailureTags';
import { listFailureOccurrences, failureLink, failureAlertLink } from 'features/agent-analytics/components/failures/failuresApi';
import { openAsk } from 'shared/askDraft';
import { fileIssuePrompt } from 'features/agent-analytics/components/failures/fileIssuePrompt';
import { LABEL, Section, CodeBlock } from 'features/issues/components/DetailParts';
import {
  drawerIconButtonClass,
  tableHeaderCellClass,
  tableDataCellClass,
  tableRowClass,
  tableHeaderRowClass,
} from 'features/activity/styles/dataTableStyles';

const PAGE = 20;
const dash = <span className="text-[color:var(--text-muted)]">—</span>;

/**
 * One Failures row, opened: what it is, how often, and every occurrence.
 * An occurrence opens its run's trace inside this drawer with the failing
 * span selected.
 */
export default function FailureDrawer({
  teamId, cluster, period, search: tableSearch = '', days, onClose, onPrev, onNext, hasPrev = false, hasNext = false,
}) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [trace, setTrace] = useState(null);

  useEffect(() => {
    let live = true;
    setItems([]);
    setNextBefore(null);
    setTrace(null);
    setLoading(true);
    setError(null);
    listFailureOccurrences(teamId, cluster, { period, search: tableSearch, limit: PAGE })
      .then((r) => { if (live) { setItems(r.items); setNextBefore(r.nextBefore); } })
      .catch((e) => { if (live) setError(e?.response?.data?.error || e?.message || 'Failed to load failures'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [teamId, cluster, period, tableSearch]);

  const loadMore = useCallback(() => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    listFailureOccurrences(teamId, cluster, { period, search: tableSearch, before: nextBefore, limit: PAGE })
      .then((r) => { setItems((prev) => [...prev, ...r.items]); setNextBefore(r.nextBefore); })
      .catch((e) => setError(e?.response?.data?.error || e?.message || 'Failed to load failures'))
      .finally(() => setLoadingMore(false));
  }, [teamId, cluster, period, tableSearch, nextBefore, loadingMore]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (trace) { setTrace(null); return; }
      onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, trace]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => [i.error_message, i.distinct_id, i.conversation_id, i.run_id, i.status_code]
      .some((v) => v != null && String(v).toLowerCase().includes(q)));
  }, [items, search]);

  const isRun = cluster.kind === 'run';
  const title = isRun ? cluster.agent_name : cluster.tool || cluster.agent_name;
  const users = cluster.users ? `${cluster.users.toLocaleString()}${cluster.users_is_lower_bound ? '+' : ''}` : '—';
  const cells = [
    { label: 'Agent', content: cluster.agent_name || '—', title: cluster.agent_name },
    { label: isRun ? 'Where' : 'Tool', content: isRun ? 'Run' : cluster.tool || '—', title: cluster.tool },
    { label: 'Failures', content: <span className="tabular-nums">{cluster.count.toLocaleString()}</span> },
    { label: 'Users', content: <span className="tabular-nums">{users}</span> },
    { label: 'First seen', content: <span title={formatDate(cluster.first_seen)}>{formatRelativeTime(cluster.first_seen)}</span> },
    { label: 'Last seen', content: <span title={formatDate(cluster.last_seen)}>{formatRelativeTime(cluster.last_seen)}</span> },
    {
      label: 'Per day',
      content: (
        <Sparkline
          counts={(days || []).map((d, i) => ({ bucket: d, count: cluster.daily?.[i] || 0 }))}
          width={120}
          height={20}
          compact
        />
      ),
    },
  ];
  const actionClass = 'inline-flex h-7 shrink-0 items-center whitespace-nowrap gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 text-[11px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover disabled:opacity-50';

  return (
    <div className={DRAWER_OVERLAY_CLASS} role="region" aria-label="Failure detail">
      <DrawerScrim onClose={onClose} />
      <section
        className="pointer-events-auto relative flex h-full w-[min(92vw,880px)] flex-col overflow-hidden border-l border-t border-[color:var(--border-default)] bg-surface shadow-2xl"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <div className="flex items-center gap-3 px-4 pt-4">
          <div className="flex items-center gap-1">
            <button type="button" onClick={onPrev} disabled={!hasPrev} title="Previous failure" className={drawerIconButtonClass}>
              <ChevronLeft size={16} />
            </button>
            <button type="button" onClick={onNext} disabled={!hasNext} title="Next failure" className={drawerIconButtonClass}>
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="relative min-w-0 flex-1">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape' && search) { e.stopPropagation(); setSearch(''); } }}
              placeholder="Search occurrences: message, user, run…"
              className="h-8 w-full rounded-lg bg-[color:var(--surface-sunken)] pl-8 pr-7 text-[12px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[color:var(--trodo-primary)]"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear search"
                className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
              >
                <X size={13} />
              </button>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* Opens Ask with the request drafted; Enter sends it and Ask files
                it — attaching to a similar issue, or finding the root cause and
                filing one. Closed so the Ask panel is in view. */}
            <button
              type="button"
              onClick={() => { openAsk(fileIssuePrompt({ cluster, items, period })); onClose?.(); }}
              disabled={loading || !items.length}
              className={actionClass}
            >
              <AskIcon size={13} />
              File issue
            </button>
            <button
              type="button"
              onClick={() => navigate(failureAlertLink(teamId, cluster))}
              aria-label="Create alert"
              title="Create alert"
              className={`${actionClass} w-7 justify-center px-0`}
            >
              <Bell size={16} strokeWidth={1.75} aria-hidden />
            </button>
            <button type="button" onClick={() => navigate(failureLink(teamId, cluster, period))} className={actionClass}>
              Open in Traces
            </button>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className={drawerIconButtonClass}>
            <X size={16} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pb-4 pt-4">
          <div className="shrink-0">
            <FailureTags cluster={cluster} />
            <h2 className="mt-2 break-words font-mono text-[15px] font-semibold leading-snug text-[color:var(--text-primary)]">
              {title}
            </h2>
          </div>

          <MetaGrid cells={cells} />

          {cluster.message ? (
            <Section title="Error">
              <CodeBlock tone="error">{cluster.message}</CodeBlock>
            </Section>
          ) : null}

          <div className="flex shrink-0 flex-col">
            <h3 className={`${LABEL} mb-2`}>Occurrences</h3>

            {error ? (
              <div className="mb-2 rounded-md border border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] p-2 text-[12px] text-[color:var(--feedback-error)]">
                {error}
              </div>
            ) : null}

            {loading ? (
              <div className="flex h-40 items-center justify-center"><TrodoLoader label="Loading occurrences" /></div>
            ) : rows.length === 0 ? (
              <div className="flex h-24 items-center justify-center text-[12px] text-[color:var(--text-muted)]">
                {search ? 'No occurrences match.' : 'No occurrences in this window.'}
              </div>
            ) : (
              <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] table-fixed text-left">
                    <colgroup>
                      <col style={{ width: '16%' }} />
                      <col style={{ width: '50%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '10%' }} />
                    </colgroup>
                    <thead>
                      <tr className={tableHeaderRowClass}>
                        <th className={tableHeaderCellClass}>Time</th>
                        <th className={tableHeaderCellClass}>Error</th>
                        <th className={tableHeaderCellClass}>Status</th>
                        <th className={tableHeaderCellClass}>User</th>
                        <th className={tableHeaderCellClass}>Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((i) => (
                        <tr
                          key={i.span_id || i.run_id}
                          className={tableRowClass}
                          onClick={() => setTrace({ runId: i.run_id, spanId: i.span_id })}
                          title="Open trace"
                        >
                          <td className={tableDataCellClass}>
                            <span className="tabular-nums text-[11px] text-[color:var(--text-secondary)]" title={formatDate(i.started_at)}>
                              {formatRelativeTime(i.started_at)}
                            </span>
                          </td>
                          <td className={tableDataCellClass}>
                            <span className="line-clamp-2 break-words font-mono text-[11px] text-[color:var(--text-primary)]" title={i.error_message || ''}>
                              {i.error_message || dash}
                            </span>
                          </td>
                          <td className={tableDataCellClass}>
                            {i.status_code ? <Tag mono>{i.status_code}</Tag> : dash}
                          </td>
                          <td className={tableDataCellClass}>
                            <span className="block truncate text-[11px] text-[color:var(--text-secondary)]">{i.distinct_id || dash}</span>
                          </td>
                          <td className={tableDataCellClass}>
                            <span className="tabular-nums text-[11px] text-[color:var(--text-secondary)]">
                              {i.duration_ms != null ? formatDuration(i.duration_ms) : dash}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!loading ? (
              <TableFooter
                shown={rows.length}
                total={search || tableSearch ? null : cluster.count}
                hasMore={Boolean(nextBefore)}
                loading={loadingMore}
                onLoadMore={loadMore}
              />
            ) : null}
          </div>
        </div>

        {trace?.runId ? (
          <AgentRunDetailPage
            teamId={teamId}
            runId={trace.runId}
            initialSpanId={trace.spanId || undefined}
            contained
            onClose={() => setTrace(null)}
            onChange={() => {}}
          />
        ) : null}
      </section>
    </div>
  );
}
